import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { programApi } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  PromotionClaimRequest,
  UnifiedEnrollmentResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * The caller's own enrollments (GET /programs/enrollments/me). Server-scoped to
 * the token user — no params. The page renders each enrollment's status + fee
 * paid verbatim from the response.
 */
export function useMyEnrollments() {
  const { user } = useAuth();
  return useQuery<UnifiedEnrollmentResponse[]>({
    queryKey: [...queryKeys.classes.myEnrollments(), user?.uid],
    queryFn: () => programApi.getMyEnrollments(),
    enabled: Boolean(user),
    refetchOnMount: "always",
  });
}

/**
 * Cancel an enrollment (POST /programs/enrollments/{id}/cancel). Both outcomes
 * await revalidation so the row reflects the server's current state —
 * no optimistic fake-cancel; a failure surfaces the backend error.
 */
export function useCancelEnrollment() {
  const qc = useQueryClient();
  return useMutation<UnifiedEnrollmentResponse, unknown, string>({
    mutationFn: (enrollmentId: string) =>
      programApi.cancelEnrollment(enrollmentId),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: queryKeys.classes.myEnrollments() }),
  });
}

/**
 * Claim a promoted waitlist spot (POST /programs/enrollments/{id}/claim). Pays
 * if the track has a fee — the body carries the chosen card. Both outcomes
 * await list revalidation.
 */
export function useClaimPromotion() {
  const qc = useQueryClient();
  return useMutation<
    UnifiedEnrollmentResponse,
    unknown,
    PromotionClaimRequest & { enrollmentId: string }
  >({
    mutationFn: ({ enrollmentId, ...body }) =>
      programApi.claimPromotion(enrollmentId, body),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: queryKeys.classes.myEnrollments() }),
  });
}

/** Recover the original server-owned attempt without replacement payment inputs. */
export function useResumeEnrollmentPayment() {
  const qc = useQueryClient();
  return useMutation<UnifiedEnrollmentResponse, unknown, string>({
    mutationFn: (enrollmentId) =>
      programApi.resumeEnrollmentPayment(enrollmentId),
    retry: false,
    onSettled: () =>
      qc.invalidateQueries({ queryKey: queryKeys.classes.myEnrollments() }),
  });
}

/**
 * Decline a promoted waitlist spot (POST /programs/enrollments/{id}/decline).
 * Frees the promoted spot. Both outcomes await list revalidation.
 */
export function useDeclinePromotion() {
  const qc = useQueryClient();
  return useMutation<UnifiedEnrollmentResponse, unknown, string>({
    mutationFn: (enrollmentId: string) =>
      programApi.declinePromotion(enrollmentId),
    onSettled: () =>
      qc.invalidateQueries({ queryKey: queryKeys.classes.myEnrollments() }),
  });
}
